/**
 * TrustGate Requests Service - Client-side API wrapper for decision endpoints
 * NO DIRECT FIRESTORE WRITES - All decisions via server endpoints
 */

const FUNCTIONS_BASE = 'https://us-central1-trustgate-27ddc.cloudfunctions.net';

interface DecisionResult {
  ok: boolean;
  requestId?: string;
  status?: string;
  correlationId?: string;
  idempotent?: boolean;
  error?: {
    code: string;
    message: string;
  };
}

async function getIdToken(): Promise<string | null> {
  try {
    const user = (window as any).firebase?.auth()?.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  } catch {
    return null;
  }
}

function mapError(response: any, status: number): { code: string; message: string } {
  if (status === 403) {
    return { code: 'FORBIDDEN', message: 'Admin claim required (trustgate_admin).' };
  }
  if (status === 409) {
    return { code: 'CONFLICT', message: response.message || 'Request already executed/denied or invalid transition.' };
  }
  if (status === 404) {
    return { code: 'NOT_FOUND', message: 'Request not found.' };
  }
  if (status >= 500) {
    return { code: 'SERVER_ERROR', message: 'Server error. Please retry.' };
  }
  return { code: response.code || 'UNKNOWN', message: response.message || 'Request failed.' };
}

export async function approveRequest(
  requestId: string,
  reasonCode?: string,
  reasonText?: string
): Promise<DecisionResult> {
  const idToken = await getIdToken();
  if (!idToken) {
    return { ok: false, error: { code: 'UNAUTHORIZED', message: 'Not authenticated.' } };
  }

  try {
    const response = await fetch(`${FUNCTIONS_BASE}/approveTrustgateRequest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ requestId, reasonCode, reasonText }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { ok: false, error: mapError(data, response.status) };
    }

    return {
      ok: true,
      requestId: data.requestId,
      status: data.status,
      correlationId: data.correlationId,
      idempotent: data.idempotent,
    };
  } catch (e) {
    return {
      ok: false,
      error: {
        code: 'NETWORK_ERROR',
        message: e instanceof Error ? e.message : 'Network error.',
      },
    };
  }
}

export async function denyRequest(
  requestId: string,
  reasonCode?: string,
  reasonText?: string
): Promise<DecisionResult> {
  const idToken = await getIdToken();
  if (!idToken) {
    return { ok: false, error: { code: 'UNAUTHORIZED', message: 'Not authenticated.' } };
  }

  try {
    const response = await fetch(`${FUNCTIONS_BASE}/denyTrustgateRequest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ requestId, reasonCode, reasonText }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { ok: false, error: mapError(data, response.status) };
    }

    return {
      ok: true,
      requestId: data.requestId,
      status: data.status,
      correlationId: data.correlationId,
      idempotent: data.idempotent,
    };
  } catch (e) {
    return {
      ok: false,
      error: {
        code: 'NETWORK_ERROR',
        message: e instanceof Error ? e.message : 'Network error.',
      },
    };
  }
}

export async function reapproveRequest(requestId: string): Promise<DecisionResult> {
  const idToken = await getIdToken();
  if (!idToken) {
    return { ok: false, error: { code: 'UNAUTHORIZED', message: 'Not authenticated.' } };
  }

  try {
    const response = await fetch(`${FUNCTIONS_BASE}/reapproveTrustgateRequest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ requestId }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { ok: false, error: mapError(data, response.status) };
    }

    return {
      ok: true,
      requestId: data.requestId,
      status: data.status,
      correlationId: data.correlationId,
    };
  } catch (e) {
    return {
      ok: false,
      error: {
        code: 'NETWORK_ERROR',
        message: e instanceof Error ? e.message : 'Network error.',
      },
    };
  }
}

export async function retryExecution(requestId: string): Promise<DecisionResult> {
  const idToken = await getIdToken();
  if (!idToken) {
    return { ok: false, error: { code: 'UNAUTHORIZED', message: 'Not authenticated.' } };
  }

  try {
    const response = await fetch(`${FUNCTIONS_BASE}/retryTrustgateExecution`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ requestId }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { ok: false, error: mapError(data, response.status) };
    }

    return {
      ok: true,
      requestId: data.requestId,
      correlationId: data.correlationId,
    };
  } catch (e) {
    return {
      ok: false,
      error: {
        code: 'NETWORK_ERROR',
        message: e instanceof Error ? e.message : 'Network error.',
      },
    };
  }
}

export async function checkAdminClaim(): Promise<boolean> {
  try {
    const user = (window as any).firebase?.auth()?.currentUser;
    if (!user) return false;
    const tokenResult = await user.getIdTokenResult();
    return tokenResult.claims.trustgate_admin === true;
  } catch {
    return false;
  }
}
